import React, { useState } from 'react';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import { styled } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import PassworLockIcon from '../../assets/PasswordLockIcon.svg';

const CustomInputField = styled(TextField)(({ theme }) => ({
  height: '60px',
  color: 'white', // White text
  paddingLeft: '12px',
  borderColor: '#365F91',
  border: '1.4px solid transparent',
  background: `
    linear-gradient(black, black) padding-box,
    linear-gradient(180deg, #365f91 -4.29%, rgba(54, 95, 145, 0) 100%) border-box
  `,
  borderImageSlice: 1,
  borderRadius: '35px',

  '& .MuiInputBase-input': {
    color: 'white', // Ensure text is white inside input
    marginLeft: '8px',
  },
  '& .MuiInputLabel-root': {
    color: '#7D7D7D', // Label color
    left: '46.5px',
    marginLeft: '20.1px',
    transform: 'translate(0, +23px)', // Position the label beside the icon
  },
  '& .MuiInputLabel-shrink': {
    left: '46.5px', // Move label to top on focus
    marginLeft: '20.1px',
    color: '#7D7D7D',
    fontSize: '12px',
    transform: 'translate(0, -1px)',
    paddingTop: '8px', // Adjust position when focused
  },
  '& .MuiInputLabel-outlined.Mui-focused': {
    color: 'white', // Color of label when focused
  },
  '& .MuiFilledInput-root': {
    '&:before': {
      borderBottom: 'none', // Remove default underline
    },
    '&:after': {
      borderBottom: 'none', // Remove focus underline
    },
    '&.Mui-focused:after': {
      color: '#7D7D7D',
      borderBottom: 'none', // Disable blue focus underline
    },
  },
  '& .MuiInputBase-input::placeholder': {
    color: '#7D7D7D',
    opacity: 1,
  },
  '& .MuiInputBase-input::-webkit-input-placeholder': {
    /* Chrome, Safari */ color: '#7D7D7D',
  },
  '& .MuiInputBase-input::-moz-placeholder': {
    /* Firefox */ color: '#7D7D7D',
  },
  '& .MuiInputBase-input:-ms-input-placeholder': {
    /* Internet Explorer */ color: '#7D7D7D',
  },
  '& .MuiInputBase-input::-ms-input-placeholder': {
    /* Edge */ color: '#7D7D7D',
  },
  '& .MuiInputBase-input:focus::placeholder': {
    color: '#7D7D7D',
    opacity: 1,
  },
}));

interface PasswordInputProps {
  value: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  label: string;
}

const PasswordInput: React.FC<PasswordInputProps> = ({
  value,
  label,
  onChange,
  ...props
}) => {
  const [showPassword, setShowPassword] = useState(false);

  const handleClickShowPassword = () => {
    setShowPassword((prevShowPassword) => !prevShowPassword);
  };

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault();
  };

  return (
    <CustomInputField
      type={showPassword ? 'text' : 'password'}
      label={label}
      variant="filled"
      value={value}
      onChange={onChange}
      fullWidth
      InputProps={{
        startAdornment: (
          <InputAdornment position="start" sx={{ mb: 0.5 }}>
            <img src={PassworLockIcon} alt="Password Lock Icon" />
          </InputAdornment>
        ),
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              aria-label="toggle password visibility"
              onClick={handleClickShowPassword}
              onMouseDown={handleMouseDownPassword}
              edge="end"
              sx={{ color: 'white', marginTop: '4px', marginRight: '4px' }}
            >
              {showPassword ? <Visibility /> : <VisibilityOff />}
            </IconButton>
          </InputAdornment>
        ),
      }}
      InputLabelProps={{
        shrink: !!value, // Control shrink based on value
      }}
      {...props}
    />
  );
};

export default PasswordInput;
